import React, { useCallback, useEffect, useState } from 'react'
import {
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  Loader2Icon,
  BriefcaseIcon,
  PencilIcon,
  SaveIcon,
  XIcon,
  EyeIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useDivision } from '../contexts/DivisionContext'
import { matchesDivisionWithFallback } from '../lib/divisionFiltering'
import { computeJobDailyRevenue } from '../lib/jobRevenue'
import { PunchAuditPanel } from '../components/time/PunchAuditPanel'
import { PunchDetailsModal, type PunchDetailsEntry } from '../components/time/PunchDetailsModal'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileStatStrip,
  mobile,
} from '../components/mobile'

interface TimeEntryRow {
  id: string
  employee_id: string | null
  job_id: string | null
  entry_type?: string | null
  clock_in_time: string | null
  clock_out_time: string | null
  total_hours: number | null
  is_approved: boolean | null
  approved_by: string | null
  approved_at: string | null
  notes: string | null
  clock_in_latitude?: number | null
  clock_in_longitude?: number | null
  clock_out_latitude?: number | null
  clock_out_longitude?: number | null
  clock_in_accuracy_m?: number | null
  clock_out_accuracy_m?: number | null
  clock_in_distance_ft?: number | null
  clock_out_distance_ft?: number | null
  employee?: { first_name?: string; last_name?: string } | null
  job?: {
    title?: string
    job_number?: string
    operational_division_id?: string | null
    service_type?: string | null
  } | null
}

interface EmployeeOption {
  id: string
  first_name: string
  last_name: string
}

interface CompletedJob {
  id: string
  title: string
  job_number?: string
  status: string
  scheduled_date?: string
  actual_start_time?: string
  actual_end_time?: string
  actual_amount?: number
  quoted_amount?: number
  billing_type?: string | null
  operational_division_id?: string | null
  service_type?: string | null
  customer?: { company_name: string } | null
  billing_contract?: { billing_frequency?: string; billing_method?: string } | null
  contract?: { billing_frequency?: string; billing_method?: string } | null
}

function toLocalInputValue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function TimeReview() {
  const { user } = useAuth()
  const { currentDivision } = useDivision()
  const [tab, setTab] = useState<'entries' | 'summary'>('entries')

  // Time entries tab state
  const [entries, setEntries] = useState<TimeEntryRow[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [entriesLoading, setEntriesLoading] = useState(true)
  const [entriesError, setEntriesError] = useState<string | null>(null)
  const [employeeFilter, setEmployeeFilter] = useState('all')
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending' | 'approved'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editClockIn, setEditClockIn] = useState('')
  const [editClockOut, setEditClockOut] = useState('')
  const [saving, setSaving] = useState(false)
  const [detailEntry, setDetailEntry] = useState<PunchDetailsEntry | null>(null)

  // Job hours summary tab (legacy view)
  const [jobs, setJobs] = useState<CompletedJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [summaryFilter, setSummaryFilter] = useState<'all' | 'this_week' | 'this_month'>('this_week')

  const fetchEmployees = useCallback(async () => {
    const { data } = await supabase.from('employees').select('id, first_name, last_name').order('first_name')
    setEmployees((data || []) as EmployeeOption[])
  }, [])

  const fetchEntries = useCallback(async () => {
    try {
      setEntriesLoading(true)
      setEntriesError(null)
      let query = supabase
        .from('time_entries')
        .select(
          'id, employee_id, job_id, entry_type, clock_in_time, clock_out_time, total_hours, is_approved, approved_by, approved_at, notes, clock_in_latitude, clock_in_longitude, clock_out_latitude, clock_out_longitude, clock_in_accuracy_m, clock_out_accuracy_m, clock_in_distance_ft, clock_out_distance_ft, employee:employees(first_name, last_name), job:jobs(title, job_number, operational_division_id, service_type)'
        )
        .order('clock_in_time', { ascending: false })
        .limit(200)

      if (employeeFilter !== 'all') query = query.eq('employee_id', employeeFilter)
      if (dateFrom) query = query.gte('clock_in_time', `${dateFrom}T00:00:00`)
      if (dateTo) query = query.lte('clock_in_time', `${dateTo}T23:59:59`)
      if (approvalFilter === 'pending') query = query.eq('is_approved', false)
      if (approvalFilter === 'approved') query = query.eq('is_approved', true)

      const { data, error } = await query
      if (error) throw error
      setEntries(
        (data || [])
          .map((row: any) => ({
            ...row,
            employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
            job: Array.isArray(row.job) ? row.job[0] || null : row.job,
          }))
          .filter((row) => row.entry_type === 'day' || row.entry_type === 'travel' || !row.job_id || matchesDivisionWithFallback(row.job, currentDivision))
      )
    } catch (err: any) {
      console.error('Error fetching time entries:', err)
      setEntriesError(err.message || 'Failed to load time entries')
    } finally {
      setEntriesLoading(false)
    }
  }, [employeeFilter, dateFrom, dateTo, approvalFilter, currentDivision])

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  const fetchJobsSummary = useCallback(async () => {
    try {
      setJobsLoading(true)
      let query = supabase
        .from('jobs')
        .select('*, customer:customers(company_name), billing_contract:contracts!jobs_billing_contract_id_fkey(billing_frequency, billing_method), contract:contracts!jobs_contract_id_fkey(billing_frequency, billing_method)')
        .in('status', ['completed', 'pending_review', 'under_review', 'approved'])
        .order('actual_end_time', { ascending: false })

      const now = new Date()
      if (summaryFilter === 'this_week') {
        const weekStart = new Date(now)
        weekStart.setDate(now.getDate() - now.getDay())
        weekStart.setHours(0, 0, 0, 0)
        query = query.gte('actual_end_time', weekStart.toISOString())
      } else if (summaryFilter === 'this_month') {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        query = query.gte('actual_end_time', monthStart.toISOString())
      } else {
        query = query.limit(50)
      }

      const { data, error } = await query
      if (error) throw error
      setJobs((data || []).filter((job) => matchesDivisionWithFallback(job, currentDivision)))
    } catch (err) {
      console.error('Error fetching time review summary:', err)
    } finally {
      setJobsLoading(false)
    }
  }, [summaryFilter, currentDivision])

  useEffect(() => {
    if (tab === 'summary') fetchJobsSummary()
  }, [tab, fetchJobsSummary])

  const startEdit = (entry: TimeEntryRow) => {
    setEditingId(entry.id)
    setEditClockIn(toLocalInputValue(entry.clock_in_time))
    setEditClockOut(toLocalInputValue(entry.clock_out_time))
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditClockIn('')
    setEditClockOut('')
  }

  const saveEdit = async (entry: TimeEntryRow) => {
    try {
      setSaving(true)
      const clockInIso = editClockIn ? new Date(editClockIn).toISOString() : null
      const clockOutIso = editClockOut ? new Date(editClockOut).toISOString() : null
      const totalHours =
        clockInIso && clockOutIso
          ? Math.round(((new Date(clockOutIso).getTime() - new Date(clockInIso).getTime()) / (1000 * 60 * 60)) * 100) / 100
          : entry.total_hours

      const { error } = await supabase
        .from('time_entries')
        .update({
          clock_in_time: clockInIso,
          clock_out_time: clockOutIso,
          total_hours: totalHours,
          updated_at: new Date().toISOString(),
        })
        .eq('id', entry.id)
      if (error) throw error

      cancelEdit()
      await fetchEntries()
    } catch (err: any) {
      alert('Failed to save punch times: ' + (err.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async (entry: TimeEntryRow) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .update({
          is_approved: true,
          approved_by: user?.id || null,
          approved_at: new Date().toISOString(),
        })
        .eq('id', entry.id)
      if (error) throw error
      await fetchEntries()
    } catch (err: any) {
      alert('Failed to approve entry: ' + (err.message || 'Unknown error'))
    }
  }

  const handleUnapprove = async (entry: TimeEntryRow) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .update({ is_approved: false, approved_by: null, approved_at: null })
        .eq('id', entry.id)
      if (error) throw error
      await fetchEntries()
    } catch (err: any) {
      alert('Failed to unapprove entry: ' + (err.message || 'Unknown error'))
    }
  }

  const totalHours = entries
    .filter((e) => e.entry_type !== 'day')
    .reduce((sum, e) => sum + Number(e.total_hours || 0), 0)
  const travelHours = entries
    .filter((e) => e.entry_type === 'travel')
    .reduce((sum, e) => sum + Number(e.total_hours || 0), 0)
  const pendingCount = entries.filter((e) => !e.is_approved).length
  const approvedCount = entries.filter((e) => e.is_approved).length

  const calculateJobHours = (job: CompletedJob): number | null => {
    if (job.actual_start_time && job.actual_end_time) {
      const start = new Date(job.actual_start_time)
      const end = new Date(job.actual_end_time)
      const diffMs = end.getTime() - start.getTime()
      return diffMs > 0 ? diffMs / (1000 * 60 * 60) : null
    }
    return null
  }

  const summaryTotalHours = jobs.reduce((sum, job) => sum + (calculateJobHours(job) || 0), 0)
  const summaryRevenue = jobs.reduce(
    (sum, job) =>
      sum +
      computeJobDailyRevenue({
        quoted_amount: job.quoted_amount,
        actual_amount: job.actual_amount,
        scheduled_date: job.scheduled_date,
        actual_end_time: job.actual_end_time,
        billing_type: job.billing_type,
        contract: job.billing_contract ?? job.contract,
      }).actual,
    0
  )

  const TIME_REVIEW_COLUMNS = [
    { id: 'employee', label: 'Employee' },
    { id: 'type', label: 'Type' },
    { id: 'job', label: 'Job' },
    { id: 'clockIn', label: 'Clock In' },
    { id: 'clockOut', label: 'Clock Out' },
    { id: 'hours', label: 'Hours' },
    { id: 'approval', label: 'Approval' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(TIME_REVIEW_COLUMNS, { storageKey: 'time_review_entries' })

  return (
    <MobilePage>
      <MobilePageHeader
        title="Time Review"
        subtitle={<span className="hidden sm:inline">Review, edit, and approve employee time entries</span>}
      />

      <div className={`${mobile.surface} overflow-hidden`}>
        <div className="flex border-b border-gray-100 overflow-x-auto scrollbar-none">
          <button
            type="button"
            onClick={() => setTab('entries')}
            className={`px-4 min-h-[48px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
              tab === 'entries' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
            }`}
          >
            Time Entries
          </button>
          <button
            type="button"
            onClick={() => setTab('summary')}
            className={`px-4 min-h-[48px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
              tab === 'summary' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
            }`}
          >
            Job Hours Summary
          </button>
        </div>
      </div>

      {tab === 'entries' ? (
        <>
          <div className="lg:hidden">
            <MobileStatStrip
              stats={[
                { key: 'hours', label: 'Hours', value: totalHours.toFixed(1), tone: 'blue' },
                { key: 'travel', label: 'Travel', value: travelHours.toFixed(1) },
                { key: 'pending', label: 'Pending', value: pendingCount, tone: 'amber' },
                { key: 'approved', label: 'Approved', value: approvedCount, tone: 'green' },
              ]}
            />
          </div>
          <div className="hidden md:grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Hours</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{totalHours.toFixed(1)}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                  <ClockIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Travel Hours</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{travelHours.toFixed(1)}</p>
                  <p className="text-xs text-gray-400 mt-1">{entries.length} entries</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
                  <CalendarIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{pendingCount}</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
                  <ClockIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Approved</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{approvedCount}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-green-600">
                  <CheckCircleIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-wrap gap-3 items-center">
            <select
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All employees</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
            <select
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value as typeof approvalFilter)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All approval states</option>
              <option value="pending">Pending approval</option>
              <option value="approved">Approved</option>
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-gray-400">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <ColumnSelector columns={TIME_REVIEW_COLUMNS} visibleColumns={visibleColumns} onToggle={toggleColumn} />
          </div>

          {entriesError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{entriesError}</div>
          )}

          <div className="native-mobile-list space-y-2">
            {entriesLoading ? (
              <div className="p-12 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : entries.length === 0 ? (
              <MobileEmptyState title="No time entries" description="No entries for the selected filters." />
            ) : (
              entries.map((entry) => {
                const employeeName = entry.employee
                  ? `${entry.employee.first_name || ''} ${entry.employee.last_name || ''}`.trim()
                  : 'Unassigned'
                const typeLabel =
                  entry.entry_type === 'travel'
                    ? 'Travel'
                    : entry.entry_type === 'day'
                      ? 'Day'
                      : entry.entry_type === 'job'
                        ? 'Job'
                        : entry.entry_type || '—'
                const typeClass =
                  entry.entry_type === 'travel'
                    ? 'bg-purple-100 text-purple-800'
                    : entry.entry_type === 'day'
                      ? 'bg-blue-100 text-blue-800'
                      : entry.entry_type === 'job'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-700'
                const jobLabel =
                  entry.job?.title ||
                  entry.job?.job_number ||
                  (entry.entry_type === 'travel'
                    ? 'Between jobs'
                    : entry.entry_type === 'day'
                      ? 'Day clock'
                      : '—')
                return (
                  <MobileListRow
                    key={entry.id}
                    onClick={() => setDetailEntry(entry)}
                    title={employeeName}
                    subtitle={jobLabel}
                    badges={
                      <>
                        <span className={`${mobile.badge} ${typeClass}`}>{typeLabel}</span>
                        <span
                          className={`${mobile.badge} ${
                            entry.is_approved
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {entry.is_approved ? 'Approved' : 'Pending'}
                        </span>
                      </>
                    }
                    meta={
                      <>
                        <div>
                          In:{' '}
                          {entry.clock_in_time
                            ? new Date(entry.clock_in_time).toLocaleString()
                            : '—'}
                        </div>
                        <div>
                          Out:{' '}
                          {entry.clock_out_time
                            ? new Date(entry.clock_out_time).toLocaleString()
                            : '—'}
                          {entry.total_hours != null
                            ? ` · ${Number(entry.total_hours).toFixed(2)}h`
                            : ''}
                        </div>
                      </>
                    }
                    footer={
                      <>
                        <MobileRowAction onClick={() => setDetailEntry(entry)}>
                          <EyeIcon className="w-4 h-4" /> Details
                        </MobileRowAction>
                        <MobileRowAction onClick={() => startEdit(entry)}>
                          <PencilIcon className="w-4 h-4" /> Edit
                        </MobileRowAction>
                        {entry.is_approved ? (
                          <MobileRowAction onClick={() => handleUnapprove(entry)}>
                            Unapprove
                          </MobileRowAction>
                        ) : (
                          <MobileRowAction tone="success" onClick={() => handleApprove(entry)}>
                            <CheckCircleIcon className="w-4 h-4" /> Approve
                          </MobileRowAction>
                        )}
                      </>
                    }
                  />
                )
              })
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {entriesLoading ? (
              <div className="p-12 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : entries.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No time entries found for the selected filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {visibleColumns.has('employee') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>}
                      {visibleColumns.has('type') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>}
                      {visibleColumns.has('job') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>}
                      {visibleColumns.has('clockIn') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock In</th>}
                      {visibleColumns.has('clockOut') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock Out</th>}
                      {visibleColumns.has('hours') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>}
                      {visibleColumns.has('approval') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>}
                      {visibleColumns.has('actions') && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {entries.map((entry) => {
                      const isEditing = editingId === entry.id
                      const employeeName = entry.employee
                        ? `${entry.employee.first_name || ''} ${entry.employee.last_name || ''}`.trim()
                        : 'Unassigned'
                      const typeLabel =
                        entry.entry_type === 'travel'
                          ? 'Travel'
                          : entry.entry_type === 'day'
                            ? 'Day'
                            : entry.entry_type === 'job'
                              ? 'Job'
                              : entry.entry_type || '—'
                      const typeClass =
                        entry.entry_type === 'travel'
                          ? 'bg-purple-100 text-purple-800'
                          : entry.entry_type === 'day'
                            ? 'bg-blue-100 text-blue-800'
                            : entry.entry_type === 'job'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-700'
                      return (
                        <tr
                          key={entry.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            if (!isEditing) setDetailEntry(entry)
                          }}
                        >
                          {visibleColumns.has('employee') && (
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{employeeName}</td>
                          )}
                          {visibleColumns.has('type') && (
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${typeClass}`}>
                                {typeLabel}
                              </span>
                            </td>
                          )}
                          {visibleColumns.has('job') && (
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {entry.job?.title ||
                                entry.job?.job_number ||
                                (entry.entry_type === 'travel' ? 'Between jobs' : entry.entry_type === 'day' ? 'Day clock' : '—')}
                            </td>
                          )}
                          {visibleColumns.has('clockIn') && (
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  value={editClockIn}
                                  onChange={(e) => setEditClockIn(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                              ) : entry.clock_in_time ? (
                                new Date(entry.clock_in_time).toLocaleString()
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          {visibleColumns.has('clockOut') && (
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  value={editClockOut}
                                  onChange={(e) => setEditClockOut(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                              ) : entry.clock_out_time ? (
                                new Date(entry.clock_out_time).toLocaleString()
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          {visibleColumns.has('hours') && (
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {entry.total_hours != null ? `${Number(entry.total_hours).toFixed(2)}h` : '—'}
                            </td>
                          )}
                          {visibleColumns.has('approval') && (
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  entry.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {entry.is_approved ? 'Approved' : 'Pending'}
                              </span>
                            </td>
                          )}
                          {visibleColumns.has('actions') && (
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={() => saveEdit(entry)}
                                      disabled={saving}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-md disabled:opacity-50"
                                      title="Save"
                                    >
                                      <SaveIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-md"
                                      title="Cancel"
                                    >
                                      <XIcon className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setDetailEntry(entry)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md"
                                      title="Punch details"
                                    >
                                      <EyeIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => startEdit(entry)}
                                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md"
                                      title="Edit punch times"
                                    >
                                      <PencilIcon className="w-4 h-4" />
                                    </button>
                                    {entry.is_approved ? (
                                      <button
                                        onClick={() => handleUnapprove(entry)}
                                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                                      >
                                        Unapprove
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleApprove(entry)}
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
                                      >
                                        <CheckCircleIcon className="w-3.5 h-3.5" />
                                        Approve
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <PunchDetailsModal
            entry={detailEntry}
            onClose={() => setDetailEntry(null)}
            onEditTimes={(e) => {
              setDetailEntry(null)
              startEdit(e as TimeEntryRow)
            }}
          />

          <PunchAuditPanel dateFrom={dateFrom} dateTo={dateTo} employeeFilter={employeeFilter} />
        </>
      ) : (
        <>
          <div className="flex justify-end">
            <select
              value={summaryFilter}
              onChange={(e) => setSummaryFilter(e.target.value as typeof summaryFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Hours</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{summaryTotalHours.toFixed(1)}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                  <ClockIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Jobs</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{jobs.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-green-600">
                  <CheckCircleIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Revenue</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">
                    ${summaryRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-green-600">
                  <BriefcaseIcon className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Jobs</h2>
            </div>
            {jobsLoading ? (
              <div className="p-12 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No jobs found for this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {jobs.map((job) => {
                      const hours = calculateJobHours(job)
                      return (
                        <tr key={job.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {job.title} {job.job_number && <span className="text-xs text-gray-400">#{job.job_number}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{job.customer?.company_name || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {job.actual_end_time ? new Date(job.actual_end_time).toLocaleDateString() : job.scheduled_date || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{hours !== null ? `${hours.toFixed(1)}h` : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ${Number(job.actual_amount || job.quoted_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </MobilePage>
  )
}
